import { blue } from '@mui/material/colors'
import { frFR as coreFr } from '@mui/material/locale'
import { createTheme, type Theme, type ThemeOptions } from '@mui/material/styles'
import { createContext, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// -----------------------------------------------------------------------------
// Mode detection & persistence
// -----------------------------------------------------------------------------
export type ThemeMode = 'light' | 'dark'
export const PREFERS_MODE_SETTING = 'themeMode'

export const SYSTEM_THEME_MODE: ThemeMode =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'

export const CACHED_THEME_MODE: ThemeMode =
  (typeof window !== 'undefined' && (localStorage.getItem(PREFERS_MODE_SETTING) as ThemeMode)) ||
  SYSTEM_THEME_MODE

// -----------------------------------------------------------------------------
// Custom Typography variant for data/timestamps — uses JetBrains Mono.
// -----------------------------------------------------------------------------
declare module '@mui/material/styles' {
  interface TypographyVariants {
    monospaceData: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    monospaceData?: React.CSSProperties
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    monospaceData: true
  }
}

// -----------------------------------------------------------------------------
// GLOBAL_OVERRIDES — typography + component defaults
// -----------------------------------------------------------------------------
const MONO_STACK = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
const BODY_STACK = '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
const HEADING_STACK = '"Quicksand", "Inter", "Segoe UI", sans-serif'

export const GLOBAL_OVERRIDES: ThemeOptions = {
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: BODY_STACK,
    h1: { fontFamily: HEADING_STACK, fontWeight: 700 },
    h2: { fontFamily: HEADING_STACK, fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontFamily: HEADING_STACK, fontWeight: 700 },
    h4: { fontFamily: HEADING_STACK, fontWeight: 700 },
    h5: { fontFamily: HEADING_STACK, fontWeight: 700 },
    h6: { fontFamily: HEADING_STACK, fontWeight: 700 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    overline: {
      fontWeight: 600,
      letterSpacing: '0.14em',
      fontSize: '0.72rem',
    },
    monospaceData: {
      fontFamily: MONO_STACK,
      fontWeight: 500,
      fontSize: '0.82rem',
      letterSpacing: 0,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          transition: 'background-color .25s ease',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiInputBase: {
      defaultProps: {
        // Needed to prevent adding a global style for every input field
        disableInjectingGlobalStyles: true,
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, letterSpacing: 0.3 },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: (theme as Theme).shape.borderRadius,
          transition: 'background-color .12s ease',
        }),
      },
    },
  },
}

// -----------------------------------------------------------------------------
// Theme factory
// -----------------------------------------------------------------------------
export function createCustomTheme(mode: ThemeMode, language?: string): Theme {
  const isDark = mode === 'dark'

  return createTheme(
    {
      ...GLOBAL_OVERRIDES,
      palette: {
        mode,
        primary: { main: isDark ? blue[300] : blue[700] },
        secondary: { main: '#14233a' },
        background: isDark
          ? { default: '#0a1929', paper: '#132f4c' }
          : { default: '#ffffff', paper: '#ffffff' },
        divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(20,35,58,0.08)',
      },
    },
    ...(language === 'fr' ? [coreFr] : []),
  )
}

// -----------------------------------------------------------------------------
// ColorModeContext
// -----------------------------------------------------------------------------
export interface ColorModeContextValue {
  mode: ThemeMode
  toggleColorMode: () => void
}

export const ColorModeContext = createContext<ColorModeContextValue>({
  mode: CACHED_THEME_MODE,
  toggleColorMode: () => {},
})

export function useColorMode(): ColorModeContextValue {
  return useContext(ColorModeContext)
}

// -----------------------------------------------------------------------------
// useCustomTheme — memoized theme for the current mode + i18n language.
// -----------------------------------------------------------------------------
export function useCustomTheme(mode: ThemeMode): Theme {
  const {
    i18n: { resolvedLanguage },
  } = useTranslation()

  return useMemo(() => createCustomTheme(mode, resolvedLanguage), [mode, resolvedLanguage])
}
